import React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { getContentOptions } from "../../utils/utils.js";

const CONTENT_TYPES = ["movie", "tvshow", "book"];

/**
 * "October 2026" ("Oct 2026" when `short`), with the year a shade fainter;
 * "No finish date" if undated
 */
const MonthLabel = ({ month, short = false }) => {
  if (!month) return "No finish date";
  const [year, monthNumber] = month.split("-").map(Number);
  const monthName = new Date(year, monthNumber - 1, 1).toLocaleDateString(
    undefined,
    { month: short ? "short" : "long" },
  );
  return (
    <>
      {monthName}{" "}
      <Box component="span" sx={{ color: "text.disabled" }}>
        {year}
      </Box>
    </>
  );
};

/**
 * The month's count per type after a faint dot — "· 🎬 5", or on All every
 * type finished that month: "· 🎬 5 📺 1 📖 2". Nothing when there are no
 * counts (yet).
 *
 * @param {number} size - Icon size in px, to sit with the label's text
 * @param {string} variant - Typography variant for the numbers
 * @param {boolean} [leadingDot] - Off when the counts stand alone, without the
 *   month label before them
 */
const MonthCounts = ({ counts, size, variant, leadingDot = true }) =>
  CONTENT_TYPES.filter((type) => counts?.[type]).map((type, index) => (
    <React.Fragment key={type}>
      {leadingDot && index === 0 && (
        <Box component="span" sx={{ color: "text.disabled" }}>
          ·
        </Box>
      )}
      <Stack direction="row" alignItems="center" spacing={0.25}>
        {React.cloneElement(getContentOptions()[type].icon, {
          sx: { fontSize: size },
        })}
        <Typography variant={variant} sx={{ lineHeight: 1 }}>
          {counts[type]}
        </Typography>
      </Stack>
    </React.Fragment>
  ));

/**
 * A quiet marker between months in a Track list grouped by month (sorted by
 * finish or log date): the month in muted text and how many fall in it, by
 * type (see MonthCounts), followed by a hairline. Deliberately
 * understated — the posters (or rows) are the main event; this only groups
 * them. Two shapes:
 *
 * - "row" (list view): a full-width line of its own between rows, with the
 *   month written out in full.
 * - "strip" (grid view): a thin header above a single poster, so the grid
 *   itself stays intact. A poster starting a month shows the label — short
 *   month, then a dot and the month's count per type (icon + number) — then
 *   the line; the month's other posters show only the line, reaching back
 *   across the gap to the poster before it (`bridgesLeft`) so a row reads as
 *   one continuous rule, broken only where a new month begins. A label wider
 *   than its poster (e.g. three types on All) runs over the next poster's
 *   line, on the page background so the line passes behind it — except from
 *   the last column, where it would run off the page: there the counts move
 *   down to the month's next poster, first in the row below (`countsPlacement`).
 *
 * @param {string|null} month - "YYYY-MM", or null for undated entries
 * @param {"row"|"strip"} variant
 * @param {boolean} [isFirst] - Row: the first needs no extra space above it
 * @param {boolean} [startsMonth] - Strip: this poster is its month's first
 * @param {boolean} [bridgesLeft] - Strip: extend the line over the gap to the
 *   poster on the left (same month, same row)
 * @param {{ movie?: number, tvshow?: number, book?: number }} [counts] - The
 *   month's finishes by type, shown with the label
 * @param {"label"|"deferred"|"carried"} [countsPlacement] - Strip: with the
 *   label (default); "deferred", left off a label that would run off the page;
 *   "carried", shown on their own by the poster they were deferred to
 */
export default function MonthHeading({
  month,
  variant,
  isFirst,
  startsMonth,
  bridgesLeft,
  counts,
  countsPlacement = "label",
}) {
  const labelSx = { fontWeight: 500, whiteSpace: "nowrap" };
  const line = <Divider sx={{ flex: 1, opacity: 0.6 }} />;

  if (variant === "strip") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: 16,
          mb: 0.75,
          // Matches the grid's 16px column gap
          ml: bridgesLeft ? -2 : 0,
        }}
      >
        {(startsMonth || countsPlacement === "carried") && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              pr: 1,
              color: "text.secondary",
              // Above the next poster's line, should the label run over it
              position: "relative",
              zIndex: 1,
              bgcolor: "background.default",
            }}
          >
            {startsMonth && (
              <Typography variant="caption" sx={{ ...labelSx, lineHeight: 1 }}>
                <MonthLabel month={month} short />
              </Typography>
            )}
            {countsPlacement !== "deferred" && (
              <MonthCounts
                counts={counts}
                size={12}
                variant="caption"
                leadingDot={startsMonth}
              />
            )}
          </Stack>
        )}
        {line}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mt: isFirst ? 0 : 1.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ color: "text.secondary" }}
      >
        <Typography variant="body2" sx={labelSx}>
          <MonthLabel month={month} />
        </Typography>
        <MonthCounts counts={counts} size={14} variant="body2" />
      </Stack>
      {line}
    </Box>
  );
}
